import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { APP_CONFIG, Settings } from 'src/app/config/settings';
import { map } from 'rxjs/operators';
import { PreStartLookupBundle } from 'src/app/credentialing/prestart/grid/models/prestart-grid-lookups';
import { PassportOption } from 'src/app/credentialing/prestart/grid/models/passport-option.model';
import { Ticket } from 'src/app/recruiting-candidates/models/recruiting-candidates-item.model';
import { serializeToQueryString } from 'src/app/shared/utilities/serialize-to-query-string';
import { CandidateItemModel } from '../../models/candidate-item.model';
import { appendQuery, arrayToQuery, combine } from 'src/app/shared/utilities/route';
import { FetchArgs, PagingToken, PagingTokenFixed } from 'src/app/shared/models/core';
import { ActiveCandidate } from '../../models/active-candidate.model';
import { SimpleIdName } from '../../models/simple-id-name.model';
import { JobExtension } from 'src/app/accountmanagers/candidates/model/job-extension.model';
import { ClinicianDataOptionModel } from '../../grid/filters/clinician-data-option-model.model';
import { PermanentCandidateItemModel } from '../../models/permanent-candidate-item.model';
import { PermCandidatesGridFetchArgs } from '../../../root-store/perm-post-offer-workflow-store/models/perm-post-offer-candidates-grid-filter.model';

@Injectable({ providedIn: 'root' })
export class CandidatesService {
    private readonly coreApiPath: string;
    private readonly _ayaConnectDomain: string;
    private readonly _lotusOneDomain: string;

    constructor(
        private readonly http: HttpClient,
        @Inject(APP_CONFIG) settings: Settings
    ) {
        this.coreApiPath = combine(settings.coreAPIUrl, 'ayanova');
        this._ayaConnectDomain = settings.ayacDomain;
        this._lotusOneDomain = settings.lotusOneDomain;
    }

    protected getPaged<T>(url: string): Observable<PagingTokenFixed<T[]>> {
        return this.http.get<PagingTokenFixed<T[]>>(url, { observe: 'response' }).pipe(
            map((resp) => {
                return {
                    total: resp.body.total,
                    data: resp.body.data,
                    filtered: resp.body.filtered
                };
            })
        );
    }

    private updateNames(data: PassportOption[]) {
        return data.map((x) => {
            x.name = `${x.firstName} ${x.lastName}`;
            return x;
        });
    }

    clearFilterCheck(filter: any): boolean {
        let shouldShowClearFilterButton = false;
        const keys = Object.keys(filter);

        keys.forEach((key) => {
            const value = filter[key];
            if (key === 'filters') {
                if (Array.isArray(value)) {
                    value.forEach((filterObj) => {
                        if (this.isPresentInFilter(filterObj.value)) {
                            shouldShowClearFilterButton = true;
                        }
                    });
                }
            } else {
                if (this.isPresentInFilter(value)) {
                    shouldShowClearFilterButton = true;
                }
            }
        });

        return shouldShowClearFilterButton;
    }

    private isPresentInFilter(value) {
        if (Array.isArray(value)) {
            return value.length > 0;
        }
        return !!value;
    }

    getWorkingCredentialingSpecialists(): Observable<PassportOption[]> {
        const url = `${this.coreApiPath}/credentialing/working-list/lookups/credentialing-specialists`;
        return this.http.get<PassportOption[]>(url).pipe(map((data) => this.updateNames(data)));
    }

    getPreStartCredentialingSpecialists(): Observable<PassportOption[]> {
        const url = `${this.coreApiPath}/credentialing/pre-start/lookups/bundle`;
        return this.http
            .post<PreStartLookupBundle>(url, { gridMode: 2 })
            .pipe(map((data) => this.updateNames(data.credentialingSpecialists)));
    }

    searchPreStartCandidates(args: any) {
        const endpoint = `${this.coreApiPath}/account-manager-candidates/pre-start`;
        const queryString = serializeToQueryString(args);

        return this.getPaged<CandidateItemModel>(`${endpoint}${queryString}`);
    }

    searchWorkingCandidates(args: any) {
        const endpoint = `${this.coreApiPath}/account-manager-candidates/working`;
        const queryString = serializeToQueryString(args);

        return this.getPaged<CandidateItemModel>(`${endpoint}${queryString}`);
    }

    searchPermanentCandidates(
        args: PermCandidatesGridFetchArgs
    ): Observable<PagingTokenFixed<PermanentCandidateItemModel[]>> {
        const endpoint = `${this.coreApiPath}/account-manager-candidates/permanent`;
        const queryString = serializeToQueryString(args);

        return this.getPaged<PermanentCandidateItemModel>(`${endpoint}${queryString}`);
    }

    exportPermanentPreStartCandidates(args: PermCandidatesGridFetchArgs): Observable<ArrayBuffer> {
        const endpoint = `${this.coreApiPath}/account-manager-candidates/permanent/pre-start/export`;
        const queryString = serializeToQueryString(args);
        return this.http.get(`${endpoint}${queryString}`, { responseType: 'arraybuffer' });
    }

    exportPermanentConfirmedCandidates(args: PermCandidatesGridFetchArgs): Observable<ArrayBuffer> {
        const endpoint = `${this.coreApiPath}/account-manager-candidates/permanent/confirmed/export`;
        const queryString = serializeToQueryString(args);
        return this.http.get(`${endpoint}${queryString}`, { responseType: 'arraybuffer' });
    }

    getCandidateOpenTickets(candidateId: number): Observable<Ticket[]> {
        const url = `${this.coreApiPath}/candidate/${candidateId}/open-tickets`;
        return this.http.get<Ticket[]>(url);
    }

    getActiveCandidates(args: FetchArgs): Observable<PagingToken<ActiveCandidate[]>> {
        let url = `${this.coreApiPath}/AccountManager/active-candidates`;

        if (args.filter) {
            for (const prop in args.filter) {
                const val = args.filter[prop];
                if (Array.isArray(val)) {
                    url = appendQuery(url, arrayToQuery(prop, val));
                } else if (val) {
                    url = appendQuery(url, `${prop}=${val}`);
                }
            }
        }

        if (args.sort && args.sort.field && args.sort.field.length) {
            url = appendQuery(url, `sortField=${args.sort.field}&sortType=${args.sort.dir || 'asc'}`);
        }

        return this.getPagedGrid(args.pageSize, args.skip, url);
    }

    private getPagedGrid<T>(pageSize: number, skip: number, url: string): Observable<PagingToken<T[]>> {
        const path = appendQuery(url, `pageSize=${pageSize}`, `skip=${skip}`);
        return this.http.get<PagingToken<T[]>>(path);
    }

    getUnitFormUrl(userId: number, unitFormId: string, isLotusOneUrlReference: boolean): string | null {
        if (userId && unitFormId) {
            let ayacDomain = this._ayaConnectDomain;
            if (isLotusOneUrlReference) {
                ayacDomain = this._lotusOneDomain;
            }
            if (ayacDomain.indexOf('http') <= 0) {
                ayacDomain = '//' + ayacDomain;
            }

            if (!ayacDomain.endsWith('/')) {
                ayacDomain = ayacDomain + '/';
            }

            return `${ayacDomain}#/units?userId=${userId}&formId=${unitFormId}&preview=true&contact=true`;
        }
        return null;
    }

    getUnitTypeNamesForGrid(unitTypes: SimpleIdName[], forTooltip = false, forExport = false): string {
        const unitTypeNames = '';
        if (!unitTypes?.length) {
            return unitTypeNames;
        }
        const allNames = unitTypes.map((u) => u.name).join(forExport ? ', ' : '<br/>');
        const visibleNames = unitTypes
            .slice(0, 3)
            .map((u) => u.name)
            .join('<br/>');
        if (unitTypes.length > 3) {
            return forTooltip || forExport ? allNames : `${visibleNames}...`;
        }
        return allNames;
    }

    getExtensionJobDetails(candidateId: number, jobId: number): Observable<JobExtension> {
        return this.http.get<JobExtension>(
            `${this.coreApiPath}/account-manager-candidates/extensions/${candidateId}/${jobId}/extension`
        );
    }

    getJobClinicians(searchString: string): Observable<ClinicianDataOptionModel[]> {
        const url = `${this.coreApiPath}/candidates/clinicians/`;

        return this.http.get<ClinicianDataOptionModel[]>(url, {
            params: new HttpParams().set('searchText', searchString)
        });
    }
}
